import React, { useEffect, useState, useCallback } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import SortableTree, { changeNodeAtPath, removeNodeAtPath, addNodeUnderParent } from "@nosferatu500/react-sortable-tree";
import "@nosferatu500/react-sortable-tree/style.css";
import { fetchNewCategories, deleteCategory, updateCategory, addNewCategory, API_BASE_URL, updateCategoryPriority, toggleCategoryVisibility } from "../services/api";
import { useRegion } from "../context/RegionContext";
import "./PageLayout.css";
import "./ManageCategoryPage.css";

function ManageCategoryPage() {
    const { selectedRegion } = useRegion();
    const [allCategories, setAllCategories] = useState([]);
    const [treeData, setTreeData] = useState([]);
    const [editingNode, setEditingNode] = useState(null);
    const [editText, setEditText] = useState("");
    const [editingPath, setEditingPath] = useState(null);

    // ✅ Build tree structure
    const buildTree = useCallback((categories, parentId = null) => {
        return categories
            .filter(cat => cat.ParentCategoryId === parentId)
            .map(cat => ({
                id: cat.Id,
                title: cat.Name,
                priority: cat.Priority,
                parentCategoryId: cat.ParentCategoryId,
                categoryKind: cat.CategoryKind,
                isHidden: !!cat.IsHidden,
                children: buildTree(categories, cat.Id),
            }));
    }, []);

    // ✅ Load categories (all regions; the tree is region-filtered below)
    const loadCategories = useCallback(async () => {
        try {
            const categories = await fetchNewCategories();
            setAllCategories(Array.isArray(categories) ? categories : []);
        } catch (error) {
            console.error("❌ Error fetching categories:", error);
        }
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    // Rebuild the tree for the active region whenever the data or the top-corner
    // region toggle changes — so the tree only shows the selected region's categories.
    useEffect(() => {
        const forRegion = allCategories.filter((c) => c.DerivativeSystemId === selectedRegion);
        setTreeData(buildTree(forRegion));
    }, [allCategories, selectedRegion, buildTree]);

    // ✅ Find node path recursively
    const findNodePath = (nodes, nodeId, path = []) => {
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].id === nodeId) {
                console.log(`✅ Found match for "${nodeId}" at path:`, [...path, i]);
                return [...path, i]; // ✅ Now returning relative path indexes
            }
    
            if (nodes[i].children && nodes[i].children.length > 0) {
                const childPath = findNodePath(nodes[i].children, nodeId, [...path, i]);
                if (childPath) return childPath;
            }
        }
        return null;
    };
    
    
    


    const removeNodeManually = (nodes, nodeId) => {
        return nodes
            .map(node => ({
                ...node,
                children: node.children ? removeNodeManually(node.children, nodeId) : []
            }))
            .filter(node => node.id !== nodeId);
    };
    
    
    const handleDelete = async (rowInfo) => {
        if (!rowInfo || !rowInfo.node) {
            console.error("❌ handleDelete: Invalid rowInfo or node", rowInfo);
            return;
        }
    
        const nodeId = rowInfo.node.id;
        if (!nodeId) {
            console.error("❌ handleDelete: Node ID is missing", rowInfo);
            return;
        }
    
        if (!window.confirm(`Are you sure you want to delete '${rowInfo.node.title}'?`)) return;
    
        try {
            console.log("🛠️ Removing node from tree...");
            const newTree = removeNodeManually(treeData, nodeId);
            setTreeData(newTree); // ✅ Update the tree without the node
    
            console.log("🛠️ Attempting to delete from database...");
            await deleteCategory(nodeId);
            console.log(`✅ Successfully deleted category from DB: ${nodeId}`);
    
        } catch (error) {
            console.error("❌ Error deleting category:", error);
        }
    };
    
    
    
    const findNodeById = (nodes, nodeId) => {
        for (let node of nodes) {
            if (node.id === nodeId) {
                return node; // ✅ Found it!
            }
            if (node.children && node.children.length > 0) {
                const childNode = findNodeById(node.children, nodeId);
                if (childNode) return childNode;
            }
        }
        return null;
    };
    
    
    
    
    
    
    
    

    // Category Ids are OPAQUE UUIDs — never derived from the name. (The old name-based
    // generateId() was removed: it regenerated the Id on every rename and orphaned children
    // + broke quantity_units.CategoryId and the NABERS/Greenstar views.)

    // ✅ Handle Adding a Subcategory
    const handleAddSubcategory = (rowInfo) => {
        const parentCategoryId = rowInfo.node.id;
        const subcategoryName = "New Subcategory";
        const newCategoryId = crypto.randomUUID();
        const newPriority = rowInfo.node.children ? rowInfo.node.children.length + 1 : 1;

        const newCategory = {
            id: newCategoryId,
            title: subcategoryName,
            priority: newPriority,
            parentCategoryId: parentCategoryId,
            children: [],
        };

        const newTree = addNodeUnderParent({
            treeData,
            parentKey: parentCategoryId,
            expandParent: true,
            getNodeKey: ({ node }) => node.id,
            newNode: newCategory,
        }).treeData;

        setTreeData(newTree);
        setEditingNode(newCategoryId);
        setEditText(subcategoryName);
        setEditingPath(findNodePath(newTree, newCategoryId));

        addNewCategory({
            id: newCategoryId,
            name: subcategoryName,
            parentCategoryId: parentCategoryId,
            priority: newPriority,
        }).catch(error => console.error("❌ Error saving new category:", error));
    };

    // ✅ Handle Input Change for Editing
    const handleEditChange = (e) => {
        setEditText(e.target.value);
    };

    // Rename updates the NAME only; the node Id is immutable.
    const updateNodeTitle = (nodes, nodeId, newName) => {
        return nodes.map(node => {
            if (node.id === nodeId) {
                return { ...node, title: newName };
            }
            return { ...node, children: updateNodeTitle(node.children || [], nodeId, newName) };
        });
    };



    const saveChanges = async () => {
        if (!editText.trim()) {
            alert("❌ Title cannot be empty.");
            return;
        }

        try {
            const oldNode = findNodeById(treeData, editingNode);
            if (!oldNode) {
                console.error("❌ Node not found for editing:", editingNode);
                return;
            }

            // Id stays the same — only the name changes.
            const updatedTree = updateNodeTitle(treeData, editingNode, editText);
            setTreeData([...updatedTree]); // ✅ Ensure frontend updates immediately

            // ✅ Update in database (no newId — the Id is immutable)
            await updateCategory(editingNode, {
                name: editText,
                parentCategoryId: oldNode.parentCategoryId || null,
                priority: oldNode.priority
            }).then(response => {
                console.log("✅ Database update response:", response);
            }).catch(error => {
                console.error("❌ Error updating database:", error);
                alert("❌ Failed to update category in the database.");
            });

            await loadCategories();
            alert(`✅ Category renamed successfully.`);

            setEditingNode(null);
            setEditingPath(null);
            setEditText("");

            console.log("✅ Successfully renamed category:", editingNode);

        } catch (error) {
            console.error("❌ Error updating category name:", error);
            alert("❌ Failed to update category.");
        }
    };
    
    
    // Recursively flip a node's isHidden flag in the local tree (optimistic update).
    const setNodeHidden = (nodes, nodeId, hidden) => {
        return nodes.map(node => {
            if (node.id === nodeId) {
                return { ...node, isHidden: hidden };
            }
            return { ...node, children: setNodeHidden(node.children || [], nodeId, hidden) };
        });
    };

    // Hide / show a category. Hidden categories are filtered out of the New Material and
    // composite pickers but still RESOLVE for historical mappings + the NABERS/Greenstar
    // views — so this safely retires emptied shadow building-element EPD roots.
    const handleToggleVisibility = async (rowInfo) => {
        const node = rowInfo?.node;
        if (!node?.id) return;
        const nextHidden = !node.isHidden;

        // Optimistic UI update, then persist; roll back on failure.
        setTreeData(prev => setNodeHidden(prev, node.id, nextHidden));
        try {
            await toggleCategoryVisibility(node.id, nextHidden);
        } catch (error) {
            console.error("❌ Failed to toggle visibility:", error);
            setTreeData(prev => setNodeHidden(prev, node.id, node.isHidden));
            alert("❌ Failed to update category visibility.");
        }
    };

    const updatePriorities = (nodes, parentCategoryId = null) => {
        nodes.forEach((node, index) => {
            node.priority = index + 1; // ✅ Assign new priority
            node.parentCategoryId = parentCategoryId; // parent from the node's CURRENT tree position (handles re-parenting)

            if (node.children && node.children.length > 0) {
                updatePriorities(node.children, node.id); // ✅ Update subcategories
            }
        });
    };


    const saveOrder = async () => {
        try {
            // Recursively flatten the whole tree so every level (main + sub) is persisted,
            // carrying each node's current parent so re-parenting sticks.
            const flatten = (nodes) =>
                nodes.flatMap(node => [
                    { id: node.id, priority: node.priority, parentCategoryId: node.parentCategoryId ?? null },
                    ...(node.children ? flatten(node.children) : []),
                ]);
            const priorityUpdates = flatten(treeData);

            if (!Array.isArray(priorityUpdates) || priorityUpdates.length === 0) {
                console.error("❌ No valid updates found!", priorityUpdates);
                alert("❌ No categories found to update.");
                return;
            }
    
            console.log("📤 Sending priority updates:", JSON.stringify({ updates: priorityUpdates }, null, 2));
    
            const response = await updateCategoryPriority(priorityUpdates); 
    
            console.log("✅ Response from server:", response);
    
            if (!response.success) {
                throw new Error(response.error || "Unknown error");
            }
    
            alert("✅ Order saved successfully!");
            await loadCategories();
    
        } catch (error) {
            console.error("❌ Error saving order:", error);
            alert("❌ Failed to save order.");
        }
    };
    
    
    
    

    return (
        <div className="category-page mc-page">
            <div className="mc-toolbar">
                <h1 className="mc-title">Manage Categories (Tree View)</h1>
                <button className="mc-save-btn" onClick={saveOrder}>
                    <span className="mc-save-btn__icon">💾</span> Save Order
                </button>
            </div>
            <p className="mc-hint">
                Drag rows by the handle to reorder. Showing categories for the region selected in the
                top bar — click <strong>Save Order</strong> to persist your changes.
            </p>
            <DndProvider backend={HTML5Backend}>
                <div className="mc-tree" style={{ height: 600 }}>
                    <SortableTree
                        treeData={treeData}
                        maxDepth={2}
                        onChange={(newTree) => {
                            setTreeData(newTree);
                            updatePriorities(newTree); // ✅ Track new order
                        }}
                        onMoveNode={({ treeData }) => setTreeData(treeData)}
                        generateNodeProps={(rowInfo) => ({
                            title: editingNode === rowInfo.node.id ? (
                                <div className="mc-edit">
                                    <input
                                        className="mc-edit__input"
                                        value={editText}
                                        onChange={handleEditChange}
                                        autoFocus
                                    />
                                    <button className="mc-btn mc-btn--save" onClick={saveChanges}>
                                        ✅ Save
                                    </button>
                                </div>
                            ) : (
                                <span className={`mc-node${rowInfo.node.isHidden ? " mc-node--hidden" : ""}`}>
                                    {rowInfo.node.title}
                                    {rowInfo.node.categoryKind === "MaterialType" && (
                                        <span className="mc-tag mc-tag--epd">Nezo EPD</span>
                                    )}
                                    {rowInfo.node.isHidden && (
                                        <span className="mc-tag mc-tag--hidden">Hidden</span>
                                    )}
                                </span>
                            ),
                            buttons: [
                                editingNode !== rowInfo.node.id && (
                                    <button
                                        className="mc-btn mc-btn--edit"
                                        onClick={() => {
                                            setEditingNode(rowInfo.node.id);
                                            setEditText(rowInfo.node.title);
                                            setEditingPath(findNodePath(treeData, rowInfo.node.id));
                                        }}
                                    >
                                        ✏️ Edit
                                    </button>
                                ),
                                <button
                                    className={`mc-btn ${rowInfo.node.isHidden ? "mc-btn--show" : "mc-btn--hide"}`}
                                    onClick={() => handleToggleVisibility(rowInfo)}
                                    title={rowInfo.node.isHidden ? "Show category (include in pickers)" : "Hide category (retire from pickers)"}
                                >
                                    {rowInfo.node.isHidden ? "👁️ Show" : "🙈 Hide"}
                                </button>,
                                <button
                                    className="mc-btn mc-btn--add mc-btn--icon"
                                    onClick={() => handleAddSubcategory(rowInfo)}
                                    title="Add subcategory"
                                >
                                    ➕
                                </button>,
                                <button
                                    className="mc-btn mc-btn--delete mc-btn--icon"
                                    onClick={() => handleDelete(rowInfo)}
                                    title="Delete category"
                                >
                                    🗑️
                                </button>,
                            ].filter(Boolean),
                        })}
                    />
                </div>
            </DndProvider>
        </div>
    );
}

export default ManageCategoryPage;
